/**
 * Legacy `crowFlightPending` + `startRandomCrowFlight`: picking the perched
 * bird — marker or chat chip, both dispatch `userSelectedSight` — flies it
 * once the camera is 60% into the fly-to. An eased fly-to covers almost no
 * distance after that, and waiting it out reads as a dead pause.
 */
import type { DatumEngine } from '@datum-sdk/engine';
import type { SightId } from '../../../shared/sightsData';
import { userSelectedSight } from '../../../state/appState';

/** Legacy `CROW_LAUNCH_AT_PROGRESS`. */
const LAUNCH_AT_PROGRESS = 0.6;
/** A pick that starts no fly-to (camera already framed) launches after this. */
const NO_FLY_TO_LAUNCH_MS = 150;

/**
 * `isAirborne` refuses a pick while the bird is up — the flight in the air plays
 * out, and a second one would be a second pose writer on one bird.
 */
export const watchCrowPick = (
  engine: DatumEngine,
  sightId: SightId | undefined,
  isAirborne: () => boolean,
  launch: () => void,
): (() => void) => {
  let pending = false;
  let timer = 0;
  const fire = (): void => {
    pending = false;
    timer = 0;
    if (!isAirborne()) launch();
  };
  const stopWatch = userSelectedSight.watch(({ id }) => {
    if (sightId == null || id !== sightId || isAirborne()) return;
    pending = true;
    clearTimeout(timer);
    timer = window.setTimeout(fire, NO_FLY_TO_LAUNCH_MS);
  });
  // The pick's own fly-to starts right after it; its duration is in ms.
  const onTransitionStart = ({ duration }: { duration: number }): void => {
    if (!pending) return;
    clearTimeout(timer);
    timer = window.setTimeout(fire, duration * LAUNCH_AT_PROGRESS);
  };
  engine.on('camera:transition-start', onTransitionStart);
  return () => {
    stopWatch();
    engine.off('camera:transition-start', onTransitionStart);
    clearTimeout(timer);
  };
};
